import { TooltipIcon } from '@ipfabric/ui-components/Tooltip/TooltipIcon';
import {
  DEBOUNCE_TEXT_INPUT_CHANGES_DEFAULT,
  useDebounce,
} from '@ipfabric/ui-components/hooks/useDebounce';
import { useLoadingState } from '@ipfabric/ui-components/hooks/useLoadingState';
import { withTargetValue } from '@ipfabric/ui-components/utils/events/withTargetValue';
import { withLoadingIndicator } from '@ipfabric/ui-components/utils/loading/withLoadingIndicator';
import type { Awaitable } from '@ipfabric/ui-components/utils/types/Awaitable';
import { isNonNullable, isNullable } from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import classNames from 'classnames';
import { Checkbox } from 'components/Checkbox/Checkbox';
import { useGlobalStateContextSelectors } from 'components/GlobalState/GlobalStateModel';
import { ScrollPaginator } from 'components/ScrollPaginator/ScrollPaginator';
import { TextInput } from 'components/TextInput/TextInput';
import { useLocale } from 'containers/App/LocaleContext';
import { useEffect, useRef, useState } from 'react';
import type { FC, RefObject } from 'react';
import type Scrollbars from 'react-custom-scrollbars-2';
import { toggleArrayElement } from 'utils/array/toggleArrayElement';
import { useOverflowPosition } from 'utils/hooks/useOverflowPosition';
import { useArrowKeyNavigation } from 'utils/keyboard/useArrowKeyNavigation';
import {
  type InfiniteScrollItemsResult,
  useInfiniteScrollItems,
} from 'utils/react/useInfiniteScrollItems';

interface Props {
  value: string[];
  onChange: (value: string[]) => void;
  wrapperRef: RefObject<HTMLElement>;
  loadValues: (
    startIndex: number,
    filter: string,
    limit?: number,
  ) => Awaitable<InfiniteScrollItemsResult<FacetedFilterAttributeValueOption>>;
  attributeName: string;
}

export type FacetedFilterAttributeValueOption = {
  value: string;
  /**
   * Option is still available but for some reason grayed out.
   *
   * When other conditions make given option unavailable (e.g. no match in conbination
   * with other filters or current discovery snapshot), it is visually indicated
   * but the option is still available for some scenarios. For example, it shouldn't
   * be automatically unchecked as the user doesn't want to lose the filter when
   * reaching a state where given value is not available, and it must be possible
   * to uncheck it still.
   */
  grayedOutReason?: string;
};

export const FacetedFilterAttributeSelect: FC<Props> = (props) => {
  const ctxProps = useGlobalStateContextSelectors({
    contentWrapper: prop('contentWrapper'),
  });
  const {
    generic: {
      components: { facetedFilter: facetedFilterMsg },
    },
  } = useLocale();
  // Actual value of input and filter is separated to allow us to debounce
  const [filterValue, setFilterValue] = useState<string>('');
  const [filter, setFilter] = useState<string>('');
  const scrollViewRef = useRef<Scrollbars>(null);
  const { handleOptionMouseEnter } = useArrowKeyNavigation({
    scrollViewRef,
    wrapperRef: props.wrapperRef,
  });
  const debounce = useDebounce(DEBOUNCE_TEXT_INPUT_CHANGES_DEFAULT);

  const makeHandleChange = (option: string) => () => {
    props.onChange(toggleArrayElement(option)(props.value));
  };

  const [selectAllState, setSelectAllState] = useLoadingState();

  const { items, loadAllItems, scrollPaginatorProps } = useInfiniteScrollItems({
    loadItems: (startIndex, limit) =>
      props.loadValues(startIndex, filter, limit),
    hoistedItems: [],
    reloadDeps: [filter],
    getItemKey: (k) => k.value,
  });

  const selectAll = async () => {
    if (isNullable(scrollPaginatorProps.total)) {
      return;
    }

    if (props.value.length === scrollPaginatorProps.total) {
      props.onChange([]);
    } else {
      if (scrollPaginatorProps.loaded === scrollPaginatorProps.total) {
        props.onChange(items.map(prop('value')));
        return;
      }

      await withLoadingIndicator(setSelectAllState)(loadAllItems);
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: This effect runs only once based on selectAllState
  useEffect(() => {
    if (selectAllState === 'LOADED') {
      props.onChange(items.map(prop('value')));
      setSelectAllState('INITIAL');
    }
  }, [selectAllState]);

  const handleSearchChange = (value: string) => {
    setFilterValue(value);

    debounce(() => {
      setFilter(value);
    });
  };

  const elementRef = useRef<HTMLDivElement>(null);
  const parent = ctxProps.contentWrapper;
  const style = useOverflowPosition(elementRef, parent);

  return (
    <div className='faceted-filter__dropdown' ref={elementRef} style={style}>
      <div className='mx-2.5 mb-2 mt-1'>{`${facetedFilterMsg.selectValues} ${props.attributeName}`}</div>
      <TextInput
        className='m-2.5'
        autoFocus
        type='search'
        placeholder={facetedFilterMsg.search}
        value={filterValue}
        onChange={withTargetValue(handleSearchChange)}
      />
      <button
        type='button'
        className={classNames('faceted-filter__attribute-option')}
        onClick={selectAll}
        tabIndex={-1}
        onMouseEnter={handleOptionMouseEnter}
      >
        {filterValue
          ? facetedFilterMsg.selectAllFiltered
          : facetedFilterMsg.selectAll}

        {selectAllState === 'LOADING' ? (
          <i className='fa-regular fa-spin fa-spinner fa-fw' />
        ) : (
          <Checkbox
            value={
              isNonNullable(scrollPaginatorProps.total)
                ? 0 < props.value.length &&
                  props.value.length < scrollPaginatorProps.total
                  ? 'indeterminate'
                  : props.value.length === scrollPaginatorProps.total
                : false
            }
          />
        )}
      </button>
      <hr className='faceted-filter__separator' />
      <div className='faceted-filter__options'>
        <ScrollPaginator
          {...scrollPaginatorProps}
          scrollViewRef={scrollViewRef}
        >
          {items.map((option) => (
            <button
              type='button'
              className={classNames(
                'faceted-filter__attribute-option',
                option.grayedOutReason && 'text-disabled',
              )}
              key={option.value}
              onClick={makeHandleChange(option.value)}
              tabIndex={-1}
              onMouseEnter={handleOptionMouseEnter}
            >
              <div className='flex flex-row items-center gap-3xs'>
                {option.value}
                {option.grayedOutReason && (
                  <TooltipIcon content={option.grayedOutReason} />
                )}
              </div>
              <Checkbox value={props.value.includes(option.value)} />
            </button>
          ))}
          {scrollPaginatorProps.total === 0 && (
            <p className='pl-4 pt-2'>
              {filterValue
                ? facetedFilterMsg.noValueFoundFilter
                : facetedFilterMsg.noValueFoundSnapshot}
            </p>
          )}
        </ScrollPaginator>
      </div>
    </div>
  );
};
